/**
 * Content-Design-authored copy for the generic multi-section step route
 * (TG-6), which serves the confirm-details step, the change-check step,
 * and the exposures step. No lorem, no placeholder, no developer-drafted
 * wording (the same standard TG-5/TG-7 hold their own copy to).
 *
 * TG-13 (ADR-20261001-rjj8) split the single combined
 * confirm-details-and-change-check entry into two step headings,
 * confirm-details and change-check, matching route-table.ts's own split
 * of that step into two routes. The change-check heading reuses the
 * already-authored `changeCheckQuestion` text verbatim rather than
 * drafting new copy — TG-16 (Content Design Agent, mandatory) owns the
 * dedicated change-check route's own authored copy; this entry only
 * keeps the existing generic multi-section route renderable for that
 * step id until TG-16 lands.
 *
 * `changeCheckQuestion` is taken verbatim from
 * `product-spec.md`'s own AC-6 scenario wording ("has anything changed
 * since your last renewal?"), not redrafted independently of it.
 *
 * Field-level labels for every OTHER field (e.g. `estimatedAnnualGross
 * Revenues`) are not Content-Design-authored copy this session — the
 * mandate this session named explicitly is section labels, the confirm
 * control label, the change-check question, and the completeness
 * statement (architecture-plan.json TG-6). `humanizeFieldId` below is a
 * deterministic, non-authored placeholder derivation, not a content
 * string, and is named as such rather than passed off as one.
 */
import type { StepId } from "../../routing/route-table";

export const renewalCaptureContent = {
  stepHeading: {
    "confirm-details": "Confirm your details",
    "change-check": "Has anything changed since your last renewal?",
    exposures: "Tell us about your exposures",
  } as Partial<Record<StepId, string>>,

  /**
   * Keyed by `SectionDescriptor.labelKey` (route-table.ts) — the ONLY
   * place a section id or label key literal lives (ADR-20260826-vbpt);
   * this map is keyed by that declared string, never by re-deriving or
   * duplicating a section id here.
   */
  sectionLabels: {
    "routing.section.businessDetails.label": "Business details",
    "routing.section.changeCheck.label": "Has anything changed?",
    "routing.section.exposureBusinessOperations.label": "Business operations",
    "routing.section.exposureCommercialProperty.label": "Commercial property",
    "routing.section.exposureCommercialAuto.label": "Commercial auto",
    "routing.section.exposureCommercialWatercraft.label": "Commercial watercraft",
    "routing.section.exposureGeneralLiability.label": "General Liability",
    "routing.section.exposureLiquorLiability.label": "Liquor liability exposures",
    "routing.section.exposureMarinaOperatorsLegalLiability.label": "Marina operators' legal liability",
    "routing.section.exposureMarineEmployersLegalLiability.label": "Marine employers' legal liability",
    "routing.section.exposureTransportation.label": "Transportation",
    "routing.section.exposureCommercialTrucking.label": "Commercial trucking",
    "routing.section.exposureWorkersCompensation.label": "Workers' compensation",
    "routing.section.exposureInlandMarineEquipmentFloater.label": "Inland marine / equipment floater",
    "routing.section.exposureClaimsAndLossHistory.label": "Claims and loss history",
    "routing.section.exposureAdditionalCoverageConsiderations.label": "Additional coverage considerations",
    "routing.section.exposureAnythingElse.label": "Anything else we should know",
  } as Record<string, string>,

  /** Verbatim from product-spec.md's AC-6 scenario. */
  changeCheckQuestion: "Has anything changed since your last renewal?",
  changeCheckOptions: {
    yes: { value: "yes" as const, label: "Yes" },
    no: { value: "no" as const, label: "No" },
  },

  confirmSectionLabel: "Confirm this section",

  /** OBJ-122/FIND-177 (AC-8, Business Rules :112-115): verbatim reuse of
   * the same Content-Design-authored string DeclarationRoute and
   * ConfirmDetailsRoute already render for their own "Save and finish
   * later" control — not a fresh content decision, the identical control
   * on the one remaining multi-section route (exposures). */
  saveAndFinishLaterLabel: "Save and finish later",

  /** OBJ-101/FIND-125 (AC-6): the control that returns a confirmed section
   * to editable, with its saved answer pre-selected, so a customer who
   * confirmed an answer in error — "has anything changed" being the
   * one-click shortest path AC-6 exists for — has a way back. Not
   * Content-Design-authored copy this session (see this file's own doc
   * comment on scope); named here rather than left silently absent. */
  changeAnswerLabel: "Change this answer",

  /** ADR-20260914-t4vk's open item ("Content Design's final wording for
   * the save-boundary statement"), closed here. Rendered on the section
   * a customer is actively filling in — the one place the "not saved
   * yet" rule actually bites. */
  saveBoundaryStatement:
    'Your answers in this section are saved only when you select "Confirm this section". Nothing is saved before then.',

  /** ADR-20260920-yzy4: the exposures step's own completeness statement,
   * so a section an applicability predicate filtered out reads as
   * correct rather than broken, for a low-digital-literacy audience. */
  exposuresCompletenessStatement: "These are all the sections that apply to your renewal.",

  progressLabel(confirmedCount: number, totalCount: number): string {
    return `${confirmedCount} of ${totalCount} section${totalCount === 1 ? "" : "s"} completed`;
  },

  // TG-6 (AC-14): the H2 section-list screen's own content, verbatim from
  // design_handoff_renewals_portal_H2/screens/04-05-GuidedExposures.dc.html.
  /** H2 shortens the heading below 600px. */
  sectionListHeadingNarrow: "Review exposures",
  sectionListHeading: "Review your exposures",
  sectionListEyebrow: "Step 2 of 3",
  /** H2 README.md:183-216, `04-05-GuidedExposures.dc.html`'s
   * `toggleExposuresHelp`/`exposuresHelpExpanded` -- the 44px help icon
   * button inline after the "Review your exposures" heading (owner
   * only), and the panel it reveals. Visually-hidden trigger label and
   * panel body, both verbatim from H2. */
  exposuresHelpTriggerLabel: "What are exposures?",
  exposuresHelpText:
    "Exposures are the things about your business that affect your insurance, like how many staff you have, where you work, and what vehicles you use.",
  /** ADR-20261016-v6nd (AC-2's "section-list card and timeline"): the
   * white card's own heading2, verbatim from H2's
   * `04-05-GuidedExposures.dc.html` (`railHead`'s `<h2>`, "Your
   * sections") and its screenshots -- missing from the original build,
   * which rendered the meta line and the row list directly on the page
   * background with no card and no heading of their own. */
  sectionListCardHeading: "Your sections",
  /** The card header's right-aligned "Select to assign" label (owner,
   * shown while any section is unassigned) -- H2's `selectHeadStyle`,
   * aria-hidden there because every row's own checkbox already carries
   * an accessible name (`selectToAssignLabel` below). */
  selectToAssignHeaderLabel: "Select to assign",
  sectionListIntroOwner:
    "Choose a section to review. If another team member is better suited to complete a particular section, you may assign it to them for review and completion.",
  sectionListIntroContributor: "Choose a section to review.",
  /** H2 04-05-GuidedExposures.dc.html `introText`, section view: the owner
   * and colleague wordings differ only in "your" / "this" policy. */
  sectionPageIntroOwner: "Select a help icon for more information, including details we already have on your policy.",
  sectionPageIntroContributor: "Select a help icon for more information, including details we already have on this policy.",
  sectionsCompletedStatement(completedCount: number, totalCount: number): string {
    return `You\u2019ve completed ${completedCount} of ${totalCount} sections.`;
  },
  /** H2: once a colleague has completed sections, the count names no one. */
  sectionsCompletedWithColleagueStatement(completedCount: number, totalCount: number): string {
    return `${completedCount} of ${totalCount} sections completed.`;
  },
  statusToDo: "To do",
  statusInProgress: "In progress",
  statusCompleted: "Completed",
  assignedToLabel(name: string): string {
    return `Assigned to: ${name}`;
  },
  completedByLabel(name: string): string {
    return `Completed by: ${name}`;
  },
  /** AC-23: visually hidden after each section title in the list. */
  sectionPositionText: (position: number, total: number) => `, section ${position} of ${total}`,
  policyNumberLabel(policyNumber: string): string {
    return `Policy number ${policyNumber}`;
  },
  selectToAssignLabel(title: string, policyNumber: string | undefined): string {
    const policyNumberSuffix = policyNumber !== undefined ? ` policy number ${policyNumber}` : "";
    return `Select ${title}${policyNumberSuffix} to assign`;
  },
  needHelpOwner: "Need help? Contact your Account Manager.",
  needHelpContributor: "Need help? Contact the policy owner.",
  assignToColleagueLabel: "Assign to a colleague",
  reassignLabel: "Reassign",
  continueAndSubmitLabel: "Continue and submit",
  continueLabel: "Continue",
  /** H2 README.md:241 (exposure section page's action row): "Back"
   * (secondary), shown "from section 2" -- i.e. whenever a previous
   * section/page exists in the ordered list "Section N of M" counts
   * against. Distinct from `backToSectionsLabel` below, which is the
   * link above the card, not an action-row button. */
  previousSectionLabel: "Back",
  backToSectionsLabel: "Back to your sections",
  /** H2 README.md:185-186, FIX-1 (m1-portal-design-fixes): the section
   * list's own back link -- owner only, shown only above the list view,
   * never the section view (which keeps `backToSectionsLabel` above). */
  backToConfirmDetailsLabel: "Back to confirm details",
  additionalDetailsTitle(sectionTitle: string): string {
    return `${sectionTitle} - additional details`;
  },
  sectionOfLabel(order: number, total: number): string {
    return `Section ${order} of ${total}`;
  },
  // TG-6 (AC-18): the owner-facing panels for an assigned-but-not-complete
  // section, and a colleague-completed one (H2 and AC-18 both use U+2019).
  assignedHelpPanel(name: string): string {
    return `${name} is completing this section. You\u2019ll be able to see their responses once they\u2019re done.`;
  },
  completedByColleaguePanel(name: string): string {
    return `${name} completed this section. You can change any response before you submit.`;
  },
  // TG-6 (AC-16): the Assign dialog.
  assignDialogTitle: "Assign to a colleague",
  assignDialogIntro: "We will email them a link that only opens the sections you have selected:",
  assignDialogSubmitLabel: "Assign",
  dialogCancelLabel: "Cancel",
  theirNameLabel: "Their name",
  theirEmailLabel: "Their email address",
  /** m1-portal-delegation TG-3, AC-17: Content Design Agent-authored copy
   * for the client-side mirror of the server's assignee-name rule
   * (`src/api/functions/assignments.py`, 422 `invalid_request`) -- an
   * empty name is still "Enter their name" (`assign.assigneeName`/
   * `missing`, error-registry.ts); this single message covers every OTHER
   * rejection reason (over 100 characters, a line break, or text that
   * looks like an email address, web address or phone number), so the
   * Assign and Reassign dialogs show one error for the whole rule rather
   * than four. Deliberately NOT in error-registry.ts: the architecture
   * plan names content.ts as this string's home. Wording approved under
   * ADR-20261030-922w (2026-10-30, copy from Aoife). */
  assigneeNameFormatError: "Enter their name only, without an email address, web address or phone number",
  assigneeNameFormatErrorSummary: "Enter their name only",
  // TG-6 (AC-17): the Reassign dialog and the status panel shown above the
  // list after either option.
  reassignDialogTitle: "Reassign this section",
  reassignDialogIntroPrefix: "You assigned ",
  reassignDialogIntroJoin: " to ",
  // ADR-20261016-k3pd: the owner's assignment read now carries the colleague's
  // email address, so the intro line closes with it exactly as H2 does --
  // "You assigned <title> to <name> (<email>)."
  reassignDialogIntroEmailOpen: " (",
  reassignDialogIntroEmailClose: ").",
  reassignChoiceLegend: "What would you like to do?",
  reassignOptionSomeoneElse: "Assign it to someone else",
  reassignOptionSomeoneElseHelp: "We will cancel their link and email a new one to the person you choose",
  reassignOptionYourself: "Complete it yourself",
  /** m1-portal-delegation TG-3 (AC-17/architecture plan): Content Design
   * Agent authored wording for the "Complete it yourself" option -- fixed,
   * exact text, not open for redrafting at implementation time. Pairs with
   * `reassignOptionSomeoneElseHelp` above so both options in the radio
   * group carry a consequence statement, not just one. */
  reassignOptionYourselfHelp: "We will cancel their link. Any responses they already gave will be lost",
  reassignDialogConfirmLabel: "Confirm",
  statusPanelReassigned(title: string, name: string, email: string): string {
    return `We have cancelled the old link and assigned ${title} to ${name} (${email}).`;
  },
  statusPanelTakenBack(name: string, title: string): string {
    return `We have cancelled the link we sent to ${name}. You can now complete ${title} yourself.`;
  },
  serverErrorTitle: "There is a problem",
  serverErrorMessage: "Something went wrong. Try again.",
  // TG-6 (AC-19): the contributor's own section-list copy.
  contributorSectionsHeading: "Review your sections",
  submitLabel: "Submit",
  contributorIncompleteMessage(title: string): string {
    return `Complete ${title} before you submit`;
  },
} as const;

/** Looks up a section's label by its declared `labelKey`. Falls back to
 * the key itself only if a future section descriptor's label has not yet
 * had its copy authored — never fabricates a business value, only a
 * display label, and the fallback is visibly the raw key rather than a
 * plausible-looking invented phrase. */
export function getSectionLabel(labelKey: string): string {
  if (labelKey.endsWith(ADDITIONAL_DETAILS_LABEL_SUFFIX)) {
    const parentKey = labelKey.slice(0, -ADDITIONAL_DETAILS_LABEL_SUFFIX.length);
    return renewalCaptureContent.additionalDetailsTitle(getSectionLabel(parentKey));
  }
  return renewalCaptureContent.sectionLabels[labelKey] ?? labelKey;
}

/** state/section-instances.ts labels an inserted additional-details page
 * `<parent labelKey>.additionalDetails`; EXPOSURE-QUESTIONS.md titles it
 * "<Section> - additional details". */
const ADDITIONAL_DETAILS_LABEL_SUFFIX = ".additionalDetails";

/** Deterministic, NON-content-authored placeholder label for a capture
 * field with no Content-Design copy yet (see this module's own doc
 * comment). "estimatedAnnualGrossRevenues" -> "Estimated annual gross
 * revenues". */
export function humanizeFieldId(fieldId: string): string {
  const spaced = fieldId.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
